"use client";

import { useTranslations } from "next-intl";
import SolarIcon from "@/components/ui/SolarIcon/SolarIcon";
import { useSound } from "@/contexts/SoundContext";
import { isBebotVoiceOn } from "@/lib/bebotVoiceSwitch";
import styles from "./SoundSettings.module.css";

// Pastille d'icône neutre : l'émeraude est réservée aux contrôles (curseur, toggle)
function IconChip({ name }) {
    return (
        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-[10px] border border-gray-100 bg-gray-50 text-gray-500 dark:border-white/10 dark:bg-white/[0.06] dark:text-grayDark-400">
            <SolarIcon name={name} className="text-[15px]" />
        </span>
    );
}

// Curseur moderne + valeur en pourcentage
function Slider({ value, onChange, disabled, ariaLabel }) {
    const pct = Math.round(value * 100);
    return (
        <div className="flex items-center gap-3">
            <input
                type="range"
                min={0}
                max={1}
                step={0.05}
                value={value}
                disabled={disabled}
                onChange={(e) => onChange(parseFloat(e.target.value))}
                aria-label={ariaLabel}
                style={{ "--pct": `${pct}%` }}
                className={styles.range}
            />
            <span
                className={`w-9 shrink-0 text-right text-xs font-semibold tabular-nums ${
                    disabled
                        ? "text-gray-300 dark:text-grayDark-600"
                        : "text-gray-400 dark:text-grayDark-500"
                }`}
            >
                {pct}%
            </span>
        </div>
    );
}

// Interrupteur on/off
function Toggle({ on, onToggle, ariaLabel }) {
    return (
        <button
            type="button"
            onClick={onToggle}
            aria-label={ariaLabel}
            className={`relative h-6 w-11 shrink-0 rounded-full transition-colors duration-200 ${
                on ? "bg-emerald-500" : "bg-gray-200 dark:bg-grayDark-600"
            }`}
        >
            <span
                className={`absolute left-0.5 top-0.5 h-5 w-5 rounded-full bg-white shadow-sm transition-transform duration-200 ${
                    on ? "translate-x-5" : "translate-x-0"
                }`}
            />
        </button>
    );
}

function SectionHeader({ icon, label, right }) {
    return (
        <div className="mb-3 flex items-center justify-between gap-3">
            <div className="flex items-center gap-2.5">
                <IconChip name={icon} />
                <span className="text-sm font-semibold text-gray-900 dark:text-white">
                    {label}
                </span>
            </div>
            {right}
        </div>
    );
}

/**
 * Réglages du son, partagés partout (leçons Reps, quiz, préférences, onboarding).
 * Autonome : lit et écrit directement le SoundContext (persisté côté compte).
 * Trois niveaux : volume général + effets sonores (on/off + volume) + voix de
 * BeBot (on/off + volume). bebotStylesSlot : contenu optionnel (sélecteur de
 * style de voix) rendu dans la section BeBot, uniquement dans l'onboarding.
 */
export default function SoundSettings({ bebotStylesSlot = null }) {
    const t = useTranslations();
    const {
        masterVolume,
        setMasterVolume,
        soundEnabled,
        setSoundEnabled,
        volume,
        setVolume,
        bebotVoiceEnabled,
        setBebotVoiceEnabled,
        bebotVolume,
        setBebotVolume,
    } = useSound();
    const voiceOn = isBebotVoiceOn();

    return (
        <div className="space-y-5">
            {/* Volume général */}
            <div>
                <SectionHeader
                    icon="volume-loud-linear"
                    label={t("sound_master_volume")}
                />
                <Slider
                    value={masterVolume}
                    onChange={setMasterVolume}
                    ariaLabel={t("sound_master_volume")}
                />
            </div>

            {/* Effets sonores */}
            <div>
                <SectionHeader
                    icon="music-note-linear"
                    label={t("sound_effects")}
                    right={
                        <Toggle
                            on={soundEnabled}
                            onToggle={() => setSoundEnabled(!soundEnabled)}
                            ariaLabel={t("sound_effects_toggle_aria")}
                        />
                    }
                />
                <Slider
                    value={volume}
                    onChange={setVolume}
                    disabled={!soundEnabled}
                    ariaLabel={t("sound_volume_aria")}
                />
            </div>

            {/* Voix de BeBot : rien à régler quand elle est coupée pour tout le site. */}
            {voiceOn && (
                <div>
                    <SectionHeader
                        icon="chat-round-line-linear"
                        label={t("sound_bebot_voice")}
                        right={
                            <Toggle
                                on={bebotVoiceEnabled}
                                onToggle={() =>
                                    setBebotVoiceEnabled(!bebotVoiceEnabled)
                                }
                                ariaLabel={t("sound_bebot_voice_toggle_aria")}
                            />
                        }
                    />
                    <div
                        className={`transition-opacity duration-200 ${
                            bebotVoiceEnabled
                                ? ""
                                : "opacity-40 pointer-events-none"
                        }`}
                    >
                        <Slider
                            value={bebotVolume}
                            onChange={setBebotVolume}
                            disabled={!bebotVoiceEnabled}
                            ariaLabel={t("sound_bebot_volume")}
                        />
                        {bebotStylesSlot}
                    </div>
                </div>
            )}
        </div>
    );
}
